// src/components/modals/EditAttendanceModal.jsx
import { useState, useEffect } from 'react'
import { X, Calendar, Clock, User, ChevronDown } from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const EditAttendanceModal = ({ isOpen, onClose, onSubmit, loading, attendance }) => {
  const [formData, setFormData] = useState({
    employee: '',
    date: '',
    clockIn: '',
    clockOut: '',
    status: 'present'
  })
  const [errors, setErrors] = useState({})

  // Initialize form data when attendance or modal opens
  useEffect(() => {
    if (isOpen && attendance) {
      setFormData({
        employee: attendance.employee?._id || '',
        date: attendance.date ? format(new Date(attendance.date), 'yyyy-MM-dd') : '',
        clockIn: attendance.clockIn || '',
        clockOut: attendance.clockOut || '',
        status: attendance.status || 'present'
      })
      setErrors({})
    }
  }, [isOpen, attendance])

  const validateForm = () => {
    const newErrors = {}

    // Only validate editable fields - employee and date are read-only

    // Clock-in validation
    if (formData.clockIn && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.clockIn)) {
      newErrors.clockIn = 'Clock-in must be in HH:MM format'
    }

    // Clock-out validation
    if (formData.clockOut && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.clockOut)) {
      newErrors.clockOut = 'Clock-out must be in HH:MM format'
    }

    // Check if clock-out is after clock-in
    if (formData.clockIn && formData.clockOut) {
      const [clockInHour, clockInMin] = formData.clockIn.split(':').map(Number)
      const [clockOutHour, clockOutMin] = formData.clockOut.split(':').map(Number)
      
      const clockInMinutes = clockInHour * 60 + clockInMin
      const clockOutMinutes = clockOutHour * 60 + clockOutMin
      
      if (clockOutMinutes <= clockInMinutes) {
        newErrors.clockOut = 'Clock-out must be after clock-in time'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    // Prepare data for submission - only send updatable fields as per API specification
    const submitData = {
      status: formData.status
    }

    // Add optional fields
    if (formData.clockIn) submitData.clockIn = formData.clockIn
    if (formData.clockOut) submitData.clockOut = formData.clockOut

    await onSubmit(attendance._id, submitData)
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  if (!isOpen || !attendance) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Edit Attendance</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Employee Selection - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={attendance?.employee?.fullName || 'Unknown Employee'}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                readOnly
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Employee cannot be changed when editing attendance</p>
          </div>

          {/* Date - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={formData.date}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                readOnly
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Date cannot be changed when editing attendance</p>
          </div>

          {/* Clock-in */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clock-in
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="time"
                value={formData.clockIn}
                onChange={(e) => handleChange('clockIn', e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.clockIn ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="HH:MM"
              />
            </div>
            {errors.clockIn && <p className="mt-1 text-sm text-red-600">{errors.clockIn}</p>}
          </div>

          {/* Clock-out */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clock-out
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="time"
                value={formData.clockOut}
                onChange={(e) => handleChange('clockOut', e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.clockOut ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="HH:MM"
              />
            </div>
            {errors.clockOut && <p className="mt-1 text-sm text-red-600">{errors.clockOut}</p>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <div className="relative">
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="on_leave">On Leave</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Attendance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditAttendanceModal
